import * as React from 'react';
import * as _ from 'underscore';

import { ListResult } from '../../ts/fetchers/ListResult';
import { CommonErrorCode } from '../../ts/helpers/ErrorCodes';
import { LockUnlockPermissionsState } from '../../ts/helpers/LockStatusHelper';
import { PermissionsEditability, PermissionsEditabilityLogic } from '../../ts/helpers/permissions/PermissionsEditabilityLogic';
import { PermissionsEditabilityLogicFactory } from '../../ts/helpers/permissions/PermissionsEditabilityLogicFactory';
import { PermissionsHelper } from '../../ts/helpers/PermissionsHelper';
import { ProjectContentType } from '../../ts/models/ContentTypes';
import { IGranteePermissions } from '../../ts/models/IGranteePermissions';
import { IProjectInfo } from '../../ts/models/IProjectInfo';
import { Set } from '../../ts/models/Set';
import { ExplicitPermissions, ExplicitPermissionsResult } from '../../ts/resources/ExplicitPermissions';
import { Projects } from '../../ts/resources/Projects';
import { ITypedIdWithName } from '../../ts/ServerApi/types/ITypedId';
import { ErrorState } from '../../ts/services/ErrorState';
import { LongRunningActionService } from '../../ts/services/LongRunningActionService';
import { SearchSuggestionsService } from '../../ts/services/SearchSuggestionsService';
import { ToasterService } from '../../ts/services/ToasterService';
import { AddNewPermission } from './AddNewPermission';
import { PermissionsGrid } from './PermissionsGrid';
import { PermissionsLockUnlock } from './PermissionsLockUnlock';
import { getProjectSectionGroups } from './SectionGroups';

export type PermissionsDialogProps = {
  /** Original authorizables when dialog is first opened */
  authorizables: Set<any>;

  /** Type of authorizable this dialog is for */
  authorizableType: string;

  /** Width of the permissions container */
  permissionsContainerWidth: number;

  /** Height of the permissions panel */
  permissionsPanelHeight: number;

  /** Original explicit permissions values when the dialog is first opened */
  explicitPermissions: ExplicitPermissions;

  /** The PermissionsEditabilityLogicFactory that is injected into angular when opening the dialog */
  permissionsEditabilityLogicFactory: PermissionsEditabilityLogicFactory;

  /** The ErrorState that is injected into angular when opening the dialog */
  errorState: ErrorState;

  /** The Projects Resouce that is injected into angular when opening the dialog */
  projectsResource: Projects;

  toasterService: ToasterService;

  longRunningActionService: LongRunningActionService;

  searchSuggestionsService: SearchSuggestionsService;
};

export type PermissionsDialogState = {
  editability: PermissionsEditability;
  authorizables: Set<any>; // original values are passed in as props, but future updates are handled within the component
  explicitPermissions: ListResult<IGranteePermissions>;
};

export class PermissionsDialog extends React.Component<PermissionsDialogProps, PermissionsDialogState> {

  // Note from jraymakers 2019-07-08:
  // - Probably `editability` and `explicitPermissions` should be initialized to their
  //   corresponding values in this.props, as is done for `authorizables`.
  public state: PermissionsDialogState = {
    editability: {
      editable: false,
      lockState: LockUnlockPermissionsState.Mixed,
      prompt: '',
      shortMessage: '',
      longMessage: '',
      sectionHeaderMessage: null,
    },
    authorizables: this.props.authorizables,
    explicitPermissions: null,
  };

  public componentDidMount() {
    this.updateExplicitPermissionsAndEditability();
  }

  insufficientPermissionsCheck = (result: ExplicitPermissionsResult) => {
    // handle error if user has insufficient permissions to manage permissions
    if (result.errors && _.find(result.errors, { code: CommonErrorCode.CallingUserInsufficientPermissions })) {
      this.props.errorState.go({ detailsLocKey: 'permissionsPanel_insufficientPermissions' });
    }
  }

  setStateEditability = (sourceAuthorizablesById: _.Dictionary<ITypedIdWithName>) => {
    // PermissionsEditabilityLogic generates a PermissionsEditability, which contains string values that are rendered
    const editabilityLogic: PermissionsEditabilityLogic = this.props.permissionsEditabilityLogicFactory.logicForAuthorizables(this.state.authorizables, this.props.authorizableType, sourceAuthorizablesById);
    this.setState({ editability: editabilityLogic.getEditabilityWithMessages(), });
  }

  private setExplicitPermissions = (explicitPermissions: ListResult<IGranteePermissions>) => {
    explicitPermissions.items.sort(PermissionsHelper.compareGrantees);
    this.setState({ explicitPermissions, });
  }

  updateExplicitPermissionsAndEditability = () => {
    // server request to get the explicit permissions list
    this.props.explicitPermissions.forAuthorizables({type: this.props.authorizableType, ids: this.state.authorizables.keys() })
    .then((result: ExplicitPermissionsResult) => {
      if (result) {
        this.insufficientPermissionsCheck(result);
        this.setStateEditability(result.sourceAuthorizablesById);
        this.setExplicitPermissions(result.permissionsList);
      }
    });
  }

  private updateLockState = (newLockState: LockUnlockPermissionsState): void => {
    this.setState((state) => {
      const updatedControlledPermissions: boolean = newLockState === LockUnlockPermissionsState.Locked;
      let authorizables: Set<any> = state.authorizables.clone();
      authorizables.each((project: IProjectInfo) => {
        if (project.controlledPermissionsEnabled !== updatedControlledPermissions) {
          project.controlledPermissionsEnabled = updatedControlledPermissions;
        }
      });
      return { authorizables, };
    });
  }

  public render() {
    const isProject = this.props.authorizableType === ProjectContentType.name;
    return (
      <div className="tb-permissions-panel-container">
        <div className="tb-margin-bottom-wide tb-permissions-info-wrapper">
          <p className={ 'tb-permissions-info' }>
            { this.state.editability.prompt }
          </p>
        </div>
        <div className="tb-permissions-panel-header-react tb-relative">
            { this.state.editability.editable &&
              <AddNewPermission searchSuggestionsService={ this.props.searchSuggestionsService } />
            }
            { // if there is no short message (parent project permissions unlocked and content type is not proj, view, workbook)
              // or if content is not a project do not render the PermissionsLockUnlock component
              (isProject || this.state.editability.shortMessage) &&
              <PermissionsLockUnlock
                isProject={ isProject }
                projects={ this.state.authorizables }
                projectsResource={ this.props.projectsResource }
                nonProjectMessage={ this.state.editability.shortMessage }
                updateLockStatus={ this.updateLockState }
                toasterService={ this.props.toasterService }
                longRunningActionService={ this.props.longRunningActionService }
              ></PermissionsLockUnlock>
            }
        </div>
        <div>
          { /* Example useage of permissions grid for development purposes,
               will be updated after CellTemplates are finished and the actual
               Explicit and Effective permissions grids are implemented */ }
          <PermissionsGrid
            permissions={ this.state.explicitPermissions }
            templates={ getProjectSectionGroups() }
          ></PermissionsGrid>
          <PermissionsGrid
            permissions={ this.state.explicitPermissions }
            templates={ getProjectSectionGroups() }
          ></PermissionsGrid>
        </div>
      </div>
    );
  }
}
